/**
 * Warm the signed-in role's home JS (KAN-562 follow-up) the moment the role is
 * known — at sign-in, in parallel with the tenant / 2FA checks — instead of
 * when AppShell first renders it.
 *
 * Why: on a phone with a cold cache (mobile Safari after a deploy) the role
 * home chunk downloaded only AFTER the route guards settled, under the splash.
 * AppShell's Suspense fallback took the dashboard's ONE first-load splash hold
 * while that chunk loaded, its max dwell ran out, and the splash left before
 * the home (and its welcome gate) existed — so the welcome arrived late, with
 * no glide (reproduced locally with 2.5s chunk latency on WebKit/iPhone).
 *
 * The loaders are REGISTERED by the app entry (pages/roleHomeLoaders.js) —
 * never imported here — so shared auth code (syncSession) gains no import edge
 * into every role's home tree (fieldReadRouting.guard walks those edges).
 * They resolve to the SAME modules (and chunks) as App.js's / AppShell's
 * lazy() imports, so webpack dedups: once warm, lazy() renders with no
 * fallback. Memoized per chunk; a failed fetch resets so the real navigation
 * (or a later call) retries. Fire-and-forget — never throws, never blocks.
 */
let loaders = {};
const inflight = new Map();

/** App entry only: { shell, admin, operations, billing, worker, supervisor } → () => import(...). */
export function registerRoleHomeLoaders(next) {
  loaders = { ...(next || {}) };
}

function warm(key) {
  const load = loaders[key];
  if (typeof load !== 'function' || inflight.has(load)) return;
  let p;
  try {
    p = Promise.resolve(load());
  } catch (_e) {
    return;
  }
  inflight.set(load, p.catch(() => { inflight.delete(load); }));
}

export function prefetchRoleHome(role) {
  if (!role || !Object.prototype.hasOwnProperty.call(loaders, role) || typeof loaders[role] !== 'function') return;
  warm('shell');
  warm(role);
}

/** @internal tests only */
export function __resetPrefetchRoleHomeForTests() {
  loaders = {};
  inflight.clear();
}
